import { useEffect, useRef } from 'react';
import type { CrashMapCell } from '@shared/vantaCrashMapTypes';
import { gtaToScreen, metersToScreen, type MapView } from './mapProjection';

export type HeatmapStyle = 'smooth' | 'cells';

//Rampe de couleurs (faible -> fort)
const RAMP_STOPS: [number, [number, number, number]][] = [
    [0.0, [40, 90, 255]],
    [0.3, [0, 210, 230]],
    [0.5, [60, 230, 90]],
    [0.7, [255, 225, 40]],
    [1.0, [255, 50, 30]],
];

const buildRamp = () => {
    const ramp = new Uint8ClampedArray(256 * 3);
    for (let i = 0; i < 256; i++) {
        const t = i / 255;
        let j = 0;
        while (j < RAMP_STOPS.length - 2 && t > RAMP_STOPS[j + 1][0]) j++;
        const [t0, c0] = RAMP_STOPS[j];
        const [t1, c1] = RAMP_STOPS[j + 1];
        const k = Math.min(Math.max((t - t0) / (t1 - t0), 0), 1);
        for (let c = 0; c < 3; c++) ramp[i * 3 + c] = c0[c] + (c1[c] - c0[c]) * k;
    }
    return ramp;
};
const RAMP = buildRamp();

export const rampColor = (t: number, alpha = 1) => {
    const i = Math.round(Math.min(Math.max(t, 0), 1) * 255) * 3;
    return `rgba(${RAMP[i]}, ${RAMP[i + 1]}, ${RAMP[i + 2]}, ${alpha})`;
};

export const rampCss = `linear-gradient(to right, ${RAMP_STOPS.map(([t, c]) => `rgb(${c.join(',')}) ${t * 100}%`).join(', ')})`;

/** rayon (px écran) d'une tache en mode Smooth */
export const smoothRadius = (view: MapView, cellSize: number) => Math.max(metersToScreen(view, cellSize) * 1.1, 14);

type Props = {
    view: MapView;
    cells: CrashMapCell[];
    maxWeight: number;
    cellSize: number;
    style: HeatmapStyle;
    selected?: { cx: number, cy: number } | null;
};


/**
 * Calque canvas de la heatmap (au-dessus des tuiles, sans interaction : les clics sont gérés par la carte).
 */
export default function HeatmapLayer({ view, cells, maxWeight, cellSize, style, selected }: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const shadowRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        const w = Math.round(view.width * dpr);
        const h = Math.round(view.height * dpr);
        if (canvas.width !== w || canvas.height !== h) {
            canvas.width = w;
            canvas.height = h;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, w, h);
        if (!cells.length || maxWeight <= 0) return;
        ctx.scale(dpr, dpr);

        const cellPx = metersToScreen(view, cellSize);
        const margin = Math.max(cellPx, 64);
        const visible = cells.filter((c) => {
            const { sx, sy } = gtaToScreen(view, c.x, c.y);
            return sx > -margin && sy > -margin && sx < view.width + margin && sy < view.height + margin;
        });

        if (style === 'cells') {
            for (const c of visible) {
                const t = c.weight / maxWeight;
                const { sx, sy } = gtaToScreen(view, c.x, c.y);
                const size = Math.max(cellPx, 3);
                ctx.fillStyle = rampColor(t, 0.35 + 0.45 * t);
                ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
                if (c.hot && size >= 6) {
                    ctx.strokeStyle = 'rgba(255,255,255,0.8)';
                    ctx.lineWidth = 1;
                    ctx.strokeRect(sx - size / 2 + 0.5, sy - size / 2 + 0.5, size - 1, size - 1);
                }
            }
        } else {
            //Accumulation de l'intensité dans un canvas hors écran (alpha), puis colorisation
            const shadow = shadowRef.current ?? document.createElement('canvas');
            shadowRef.current = shadow;
            const sw = Math.round(view.width);
            const sh = Math.round(view.height);
            shadow.width = sw;
            shadow.height = sh;
            const sctx = shadow.getContext('2d', { willReadFrequently: true });
            if (!sctx) return;
            sctx.clearRect(0, 0, sw, sh);
            const radius = smoothRadius(view, cellSize);
            for (const c of visible) {
                const t = Math.min(Math.max(c.weight / maxWeight, 0.25), 1);
                const { sx, sy } = gtaToScreen(view, c.x, c.y);
                const grad = sctx.createRadialGradient(sx, sy, 0, sx, sy, radius);
                grad.addColorStop(0, `rgba(0,0,0,${t})`);
                grad.addColorStop(1, 'rgba(0,0,0,0)');
                sctx.fillStyle = grad;
                sctx.fillRect(sx - radius, sy - radius, radius * 2, radius * 2);
            }
            const img = sctx.getImageData(0, 0, sw, sh);
            const px = img.data;
            for (let i = 0; i < px.length; i += 4) {
                const a = px[i + 3];
                if (!a) continue;
                const r = a * 3;
                px[i] = RAMP[r];
                px[i + 1] = RAMP[r + 1];
                px[i + 2] = RAMP[r + 2];
                px[i + 3] = Math.min(255, 40 + a * 0.85);
            }
            sctx.putImageData(img, 0, 0);
            ctx.drawImage(shadow, 0, 0, view.width, view.height);
        }

        //Cellule sélectionnée
        if (selected) {
            const c = cells.find((cell) => cell.cx === selected.cx && cell.cy === selected.cy);
            if (c) {
                const { sx, sy } = gtaToScreen(view, c.x, c.y);
                const size = Math.max(cellPx, 12);
                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = 2;
                ctx.strokeRect(sx - size / 2, sy - size / 2, size, size);
            }
        }
    }, [view, cells, maxWeight, cellSize, style, selected]);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 pointer-events-none z-10"
            style={{ width: view.width, height: view.height }}
        />
    );
}
